import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Send, Upload, X, Mail, Phone, MessageSquare, CheckCircle, Wrench } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const [attachment, setAttachment] = useState(null);
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB.");
      return;
    }

    setUploading(true);
    setAttachment(file);

    const uploadData = new FormData();
    uploadData.append("file", file);

    try {
      const response = await fetch(`${API}/contact/upload`, {
        method: "POST",
        body: uploadData
      });

      if (response.ok) {
        const data = await response.json();
        setAttachmentUrl(data.attachment_url);
        toast.success("File uploaded successfully");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to upload file");
        setAttachment(null);
      }
    } catch (error) {
      toast.error("Upload failed. Please try again.");
      setAttachment(null);
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
    setAttachmentUrl("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API}/contact/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          attachment_url: attachmentUrl || null
        })
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success("Message sent successfully!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send message");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="border-b border-border bg-card/50 backdrop-blur-sm">
          <div className="flex items-center justify-between px-4 py-3 lg:px-8">
            <Link to="/" className="flex items-center gap-2">
              <img src="/toolio-logo-new.png" alt="TOOLIO" className="h-8 sm:h-10" />
              
            </Link>
          </div>
        </header>

        <main className="p-4 lg:p-8 max-w-2xl mx-auto">
          <Card className="border-border bg-card mt-12">
            <CardContent className="py-16 text-center">
              <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
              <h1 className="font-heading text-3xl font-bold uppercase mb-4">Message Sent!</h1>
              <p className="text-muted-foreground mb-8">
                Thank you for contacting us. We&apos;ll review your message and get back to you as soon as possible.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/">
                  <Button variant="outline">Back to Home</Button>
                </Link>
                <Button className="btn-primary" onClick={() => {
                  setSubmitted(false);
                  setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
                  setAttachment(null);
                  setAttachmentUrl("");
                }}>
                  Send Another Message
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="flex items-center justify-between px-4 py-3 lg:px-8">
          <Link to="/" className="flex items-center gap-2">
            <img src="/toolio-logo-new.png" alt="TOOLIO" className="h-8 sm:h-10" />
            
          </Link>
          
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/news">
              <Button variant="ghost" className="text-foreground hover:text-primary">
                News
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" className="border-yellow-500 text-yellow-400 hover:bg-yellow-900/30">
                Login
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-4xl mx-auto">
        {/* Back Link */}
        <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-1">
            <h1 className="font-heading text-3xl lg:text-4xl font-bold uppercase mb-4">
              Get In Touch
            </h1>
            <p className="text-muted-foreground mb-8">
              Have a question, feedback, or need support? We&apos;d love to hear from you. Fill out the form and we&apos;ll get back to you as soon as possible.
            </p>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <MessageSquare className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium">General Inquiries</h3>
                  <p className="text-sm text-muted-foreground">Questions about TOOLIO features and pricing</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Wrench className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium">Technical Support</h3>
                  <p className="text-sm text-muted-foreground">Help with tool registration or account issues</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Mail className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-medium">Partnership Opportunities</h3>
                  <p className="text-sm text-muted-foreground">Interested in working with us? Let&apos;s talk</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-border bg-card">
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        className="bg-background"
                        required
                        data-testid="contact-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="bg-background"
                        required
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone (optional)</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Your phone number"
                        className="bg-background"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject">Subject *</Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="What's this about?"
                        className="bg-background"
                        required
                        data-testid="contact-subject"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us how we can help..."
                      className="bg-background min-h-[150px]"
                      required
                      data-testid="contact-message"
                    />
                  </div>

                  {/* File Upload */}
                  <div className="space-y-2">
                    <Label>Attachment (optional)</Label>
                    <p className="text-xs text-muted-foreground mb-2">
                      Upload a screenshot or document to help us understand your inquiry better. Max 10MB.
                    </p>
                    
                    {attachment ? (
                      <div className="flex items-center gap-3 p-3 bg-background rounded-lg border border-border">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{attachment.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {(attachment.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        {uploading ? (
                          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={removeAttachment}
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          accept="image/*,.pdf,.doc,.docx"
                          onChange={handleFileUpload}
                          className="hidden"
                          id="attachment-upload"
                          data-testid="contact-file-input"
                        />
                        <label htmlFor="attachment-upload">
                          <Button type="button" variant="outline" asChild className="cursor-pointer">
                            <span>
                              <Upload className="w-4 h-4 mr-2" />
                              Choose File
                            </span>
                          </Button>
                        </label>
                      </div>
                    )}
                  </div>

                  <Button 
                    type="submit" 
                    className="btn-primary w-full" 
                    disabled={submitting || uploading}
                    data-testid="contact-submit"
                  >
                    {submitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-6 py-8 lg:px-12 border-t border-border mt-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 max-w-4xl mx-auto">
          <div className="flex items-center gap-2">
            <img src="/toolio-logo-new.png" alt="TOOLIO" className="h-8" />
            
          </div>
          <p className="text-muted-foreground text-sm">
            © {new Date().getFullYear()} TOOLIO. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
